import type { CustomerLocationLabel } from './types';

/**
 * How one customer delivery location reads in the picker. Every part has a
 * fallback, because a location can legitimately have no label and a customer
 * can have no name — showing a blank row would be worse than a placeholder.
 */
export const getCustomerLocationLabel = (location: any): CustomerLocationLabel => {
  const customerName = location?.customer?.name || location?.customer?.email || 'Unknown customer';

  const customerEmail = location?.customer?.email || '';

  const locationName = location?.label || location?.fullName || 'Location';

  const address = [location?.line1, location?.city, location?.state, location?.postalCode]
    .filter(Boolean)
    .join(', ');

  return { customerName, customerEmail, locationName, address };
};

/** Flattens an infinite-query page set into one location list. */
export const flattenLocationPages = (data: any): any[] =>
  data?.pages?.flatMap((page: any) => page.locations ?? []) ?? [];

/**
 * The locations to show while editing a template.
 *
 * The search endpoint returns only what matches the current term, so the
 * locations already assigned to this template have to be merged in or they
 * disappear from the picker mid-edit — and unchecking something you cannot see
 * is not a thing a user can do deliberately.
 *
 * Search results win on collision, since they are the fresher copy. Assigned
 * rows key on `deliveryAddressId` where present: the join row carries its own
 * id, and keying on that would double-list a location that is also in the
 * search results.
 */
export const mergeEditLocations = (assignedLocations: any[], searchLocations: any[]): any[] => {
  const byId = new Map<string, any>();

  for (const location of assignedLocations ?? []) {
    const id = location.deliveryAddressId ?? location.id;

    if (!id) continue;

    byId.set(id, { ...location, id });
  }

  for (const location of searchLocations ?? []) {
    if (!location?.id) continue;

    byId.set(location.id, location);
  }

  return Array.from(byId.values());
};

/** Adds or removes one location id, without duplicates. */
export const toggleLocationId = (
  selected: string[],
  locationId: string,
  checked: boolean
): string[] => {
  if (checked) {
    return Array.from(new Set([...selected, locationId]));
  }

  return selected.filter((id) => id !== locationId);
};
